import {
  ChangeEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useAppContext } from "../hooks/useAppContext";
import { getEncoding } from "js-tiktoken";
import SendArrow from "./SVGIcons/SendArrow";
import { AnswerLoading } from "./AnswerLoading";
import useBoundStore from "../store/useBoundStore";
import { getTranslation } from "../locales/getTranslation";
import MultiLangVoiceInput from "./MultiLangVoiceInput";

interface Props {
  question: string;
  setQuestion: React.Dispatch<React.SetStateAction<string>>;
  tokens: number;
  setTokens: React.Dispatch<React.SetStateAction<number>>;
  settingPanel: boolean;
  setSettingPanel: React.Dispatch<React.SetStateAction<boolean>>;
  onSend: (question: string) => void;
  firstQuestion?: boolean;
  disabled: boolean;
  placeholder?: string;
}

export const InputQuestion = ({
  question,
  setQuestion,
  tokens,
  setTokens,
  settingPanel,
  setSettingPanel,
  onSend,
  firstQuestion,
  disabled,
  placeholder,
}: Props) => {
  const MAXTOKENS = 500;
  const { locale, theme } = useAppContext();
  const error = useBoundStore((state) => state.error);
  const setError = useBoundStore((state) => state.setError);
  const [inputError, setInputError] = useState("");
  const textbox = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    if (!textbox.current) return;

    textbox.current.style.height = "inherit";
    textbox.current.style.height = `${textbox.current.scrollHeight}px`;
  };

  const sendQuestion = () => {
    if (disabled) {
      return;
    } else if (!question.trim()) {
      setInputError(getTranslation(locale, "Please ask a question") || "");
      return;
    }

    if (tokens > MAXTOKENS) {
      setInputError(
        `${getTranslation(locale, "Number of tokens exceeds")} ${MAXTOKENS}. ${getTranslation(
          locale,
          "Please reduce the length of your question",
        )}.`,
      );
      return;
    }

    onSend(question.trim());
    setInputError("");
    setTokens(0);
    setQuestion("");
  };

  const onKeyDown = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const onQuestionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const question = e.currentTarget.value;
    setQuestion(question);
    adjustHeight();
  };

  useEffect(() => {
    let debouncer = setTimeout(() => {
      const gpt3Enc = getEncoding("gpt2");
      const encoded = gpt3Enc.encode(question);

      if (inputError && encoded.length <= MAXTOKENS) {
        setInputError("");
      }

      setTokens(encoded.length);
    }, 500);
    return () => {
      clearTimeout(debouncer);
    };
  }, [question]);

  useLayoutEffect(adjustHeight, [question]);

  return (
    <div className="relative w-full">
      {firstQuestion ? (
        <div className="flex w-full flex-col items-center gap-2">
          <div className="flex h-[50px] w-full grow items-center justify-start rounded-r5 px-3.5 py-1.5 ring-1 ring-grayscale-solid-g6">
            <div className="grow">
              <AnswerLoading className="bg-blue-solid-blue6" />
            </div>
            <div className="flex flex-col justify-end">
              <button
                className={`cursor-not-allowed rounded-[14px] p-2`}
                aria-label={placeholder}
                disabled
              >
                <SendArrow className="h-[18px] w-[18px] fill-grayscale-opacity-blevel3" />
              </button>
            </div>
          </div>
          <span className={`ml-4 h-[20px] self-start text-sm font-bold`}></span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-center gap-2">
          <div
            className={`group flex max-h-[120px] w-full items-center gap-2 rounded-r5 px-[18px] py-[8px] ring-1 ring-grayscale-solid-g2 hover:ring-grayscale-solid-g6 focus:ring-blue-solid-blue6 md:px-4 md:py-2 ${theme === "dark" ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g0"}`}
          >
            <textarea
              className={`max-h-[100px] w-full resize-none break-words ${theme === "dark" ? "bg-grayscale-solid-g2" : "bg-grayscale-solid-g0"} pr-1 text-grayscale-solid-g9 focus:outline-none focus:ring-0`}
              placeholder={placeholder}
              value={question}
              onChange={onQuestionChange}
              onKeyDown={onKeyDown}
              aria-label="Question"
              rows={1}
              ref={textbox}
            />
            {(import.meta.env.MODE === "dev" ||
              import.meta.env.MODE === "localhost") && (
              <MultiLangVoiceInput setQuestion={setQuestion} />
            )}
            <div className="flex flex-col justify-end">
              <button
                className={`cursor-pointer rounded-[14px] p-[6px] group-focus:bg-blue-solid-blue6 md:p-2`}
                aria-label={placeholder}
                onClick={sendQuestion}
              >
                <SendArrow className="h-[18px] w-[18px] fill-grayscale-solid-g10 hover:fill-blue-solid-blue6" />
              </button>
            </div>
          </div>
          <span
            className={`ml-4 h-[20px] self-start text-sm font-bold text-grayscale-solid-g10 ${tokens > MAXTOKENS ? "text-red-500" : "text-gray-700"}`}
          >
            {tokens}/{MAXTOKENS}
          </span>
        </div>
      )}
    </div>
  );
};
